import { useToast } from '@1trading/react/hooks/use-toast';
import { ArrowRightIcon } from 'lucide-react';
import { useCallback } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeadingButton } from '@/components/ui/page-heading';
import { useDetailsNext } from '@/features/institution/hooks/useDetailsNext';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { usePromise } from '@/hooks/use-promise';
import { INSTITUTION_DETAILS_VERSION } from '@/services/api/profile';

type InstitutionGoToReviewButtonProps<T extends FieldValues> = {
  onIncompleteSection?: () => void | Promise<void>;
  onSubmit: (data: T) => void | Promise<void>;
};

const InstitutionGoToReviewButton = <T extends FieldValues>({ onIncompleteSection, onSubmit }: InstitutionGoToReviewButtonProps<T>) => {
  const form = useFormContext<T>();
  const { data: detailsNext, version } = useDetailsNext(INSTITUTION_DETAILS_TYPE.ONBOARDING);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleGoToReview = useCallback(async () => {
    if (!(await form.trigger())) return;

    try {
      await onSubmit(form.getValues());
    } catch (error) {
      // The section's conversion parses every step before saving, so a later step the edit opened up fails here
      if (!(error instanceof z.ZodError)) throw error;

      toast({ description: t('institutionDetails.review.incompleteSection') });
      await onIncompleteSection?.();
    }
  }, [form, onIncompleteSection, onSubmit, t, toast]);

  const { isPending, handler } = usePromise(handleGoToReview);

  if (version !== INSTITUTION_DETAILS_VERSION.V2 || detailsNext?.key !== 'authorisation') return null;

  return (
    <PageHeadingButton floatingInPage="right" disabled={isPending} onClick={handler}>
      {t('institutionDetails.review.goToReview')}
      <ArrowRightIcon className="size-4" />
    </PageHeadingButton>
  );
};
InstitutionGoToReviewButton.displayName = 'InstitutionGoToReviewButton';

export const InstitutionGoToReviewStepperButton = <T extends FieldValues>({ onSubmit }: Pick<InstitutionGoToReviewButtonProps<T>, 'onSubmit'>) => {
  const { nextStep } = useGuardedFormStepper<T>();

  return <InstitutionGoToReviewButton onIncompleteSection={() => nextStep()} onSubmit={onSubmit} />;
};
InstitutionGoToReviewStepperButton.displayName = 'InstitutionGoToReviewStepperButton';

export default InstitutionGoToReviewButton;
